'use client';

import { ReactNode, useEffect, useState } from 'react';

import { inputStyles } from '@/styles/input';
import { fontStyles } from '@/styles/typography';

import { clamp } from '@/utils/numbers';

// Render the percentage as a plain decimal string, rounded to 2 places, with
// trailing zeros stripped. Used as the displayed value of the editable input.
const formatPercentageDraft = (value: number) => (Math.round(value * 100) / 100).toString();

// Allows in-flight numeric input: digits and an optional decimal point. A
// leading minus is permitted only when negative values are in range.
const UNSIGNED_PATTERN = /^\d*\.?\d*$/;
const SIGNED_PATTERN = /^-?\d*\.?\d*$/;

// Drafts that are syntactically valid mid-typing but not yet a finite number.
const isIncompleteDraft = (draft: string) =>
  draft === '' || draft === '.' || draft === '-' || draft === '-.';

type PercentageInputProps = {
  /** Current value, or null to render an empty field (pairs with `onClear`). */
  value: number | null;
  /** Called with the clamped value whenever the user edits to a valid number. */
  onChange: (value: number) => void;
  /**
   * Called when the user clears the field. When omitted, an empty
   * field resets to `value` on blur instead of staying empty.
   */
  onClear?: () => void;
  disabled?: boolean;
  /** Optional leading icon (e.g. an edit affordance). */
  icon?: ReactNode;
  /** Inclusive bounds applied on change and blur. Defaults to 0–100. */
  min?: number;
  max?: number;
  ariaLabel?: string;
  placeholder?: string;
  /** Caps the raw input length. */
  maxLength?: number;
  /** `pill` is the compact shortcut control. `field` matches standard text inputs. */
  variant?: 'pill' | 'field';
  error?: boolean;
};

export const PercentageInput = ({
  value,
  onChange,
  onClear,
  disabled,
  icon,
  min = 0,
  max = 100,
  ariaLabel,
  placeholder,
  maxLength,
  variant = 'pill',
  error = false,
}: PercentageInputProps) => {
  const [draft, setDraft] = useState(() => (value === null ? '' : formatPercentageDraft(value)));
  const [isEditing, setIsEditing] = useState(false);

  const pattern = min < 0 ? SIGNED_PATTERN : UNSIGNED_PATTERN;

  // Sync the input value from the external value while the user is not actively
  // editing it. While focused, the draft is the source of truth so in-flight
  // keystrokes like "12." are not clobbered by recomputed values.
  useEffect(() => {
    if (isEditing) return;
    setDraft(value === null ? '' : formatPercentageDraft(value));
  }, [value, isEditing]);

  const isField = variant === 'field';

  return (
    <div
      className={
        isField
          ? inputStyles({ error }).root()
          : fontStyles({
              variant: 'LABEL_MD',
              class: [
                'h-9 flex min-w-0 max-w-full grow justify-center items-center rounded-2xl px-4',
                'bg-token-alphalight-200 hover:bg-token-alphalight-300 gap-0.5 overflow-hidden',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text',
              ],
            })
      }
    >
      {icon}
      <input
        aria-label={ariaLabel}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        disabled={disabled}
        value={draft}
        placeholder={placeholder}
        size={isField ? undefined : Math.min(Math.max(draft.length, 1), 6)}
        maxLength={maxLength}
        onFocus={(e) => {
          setIsEditing(true);
          e.currentTarget.select();
        }}
        onChange={(e) => {
          const next = e.target.value;
          if (!pattern.test(next)) return;
          setDraft(next);
          if (isIncompleteDraft(next)) {
            if (next === '') onClear?.();
            return;
          }
          const parsed = Number(next);
          if (!Number.isFinite(parsed)) return;
          onChange(clamp(parsed, min, max));
        }}
        onBlur={() => {
          setIsEditing(false);
          const parsed = Number(draft);
          if (isIncompleteDraft(draft) || !Number.isFinite(parsed)) {
            if (draft === '' && onClear) {
              return;
            }
            setDraft(value === null ? '' : formatPercentageDraft(value));
            return;
          }
          const clamped = clamp(parsed, min, max);
          setDraft(formatPercentageDraft(clamped));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className={inputStyles({ error }).input({
          class: isField
            ? ['tabular-nums']
            : ['min-w-0 max-w-[6ch] overflow-x-auto no-scrollbar tabular-nums'],
        })}
      />
      <span
        className={
          isField ? inputStyles({ error }).accessoryRight() : fontStyles({ variant: 'LABEL_MD' })
        }
      >
        %
      </span>
    </div>
  );
};
